import {
  Body,
  Button,
  CardBackground,
  ColumnHeaderCell,
  Header,
  Row,
  Table,
  Tag,
  Typography,
  useToast,
} from "@hellocarbo/lasagne";
import { FileSpreadsheetIcon } from "lucide-react";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionFunctionArgs,
  data,
  Form,
  LoaderFunctionArgs,
  useActionData,
  useLoaderData,
  useNavigate,
  useParams,
  useSubmit,
} from "react-router";
import invariant from "tiny-invariant";

import { MatchingRow } from "@/components/ImportDeposit";
import ConfirmIntegrationtModal from "@/components/ImportDeposit/ConfirmIntegrationModal";
import PageWrapper from "@/components/ui/PageWrapper";
import { FeaturesContext } from "@/contexts";
import { TImportDeposit, TMatchingMeta, TMatchingResponse } from "@/types";
import { NB_MATCHINGS_PER_PAGE } from "@/utils/importDeposits";

import { useImportDepositContext } from "./ImportDepositSummary";

export async function loader({ params, request }: LoaderFunctionArgs) {
  const { scoreId, importId } = params;
  const url = new URL(request.url);
  const page = url.searchParams.get("page") || "1";

  invariant(scoreId, "No scoreId found!");
  invariant(importId, "No importId found!");

  const importDepositResponse = await fetch(
    `/api/client/scores/${scoreId}/import_deposits/${importId}/`
  );
  if (importDepositResponse.status !== 200) {
    throw data("Failed to load this Import Deposit", {
      status: importDepositResponse.status,
    });
  }

  const { data: importDeposit } = (await importDepositResponse.json()) as {
    data: TImportDeposit;
  };

  const importDepositMatchingResponse = await fetch(
    `/api/client/scores/${scoreId}/import_deposits/${importId}/matching_entries?${new URLSearchParams({ page: page, limit: String(NB_MATCHINGS_PER_PAGE) })}`
  );
  if (importDepositMatchingResponse.status !== 200) {
    throw data("Failed to load the matching for this Import Deposit", {
      status: importDepositMatchingResponse.status,
    });
  }
  const { data: matchings, meta } =
    (await importDepositMatchingResponse.json()) as {
      data: Array<TMatchingResponse>;
      meta: TMatchingMeta;
    };

  return { importDeposit, matchings, meta, url };
}

export async function action({ request }: ActionFunctionArgs) {
  if (request.method === "PATCH") {
    const formData = await request.formData();
    const scoreId = formData.get("scoreId");
    const importId = formData.get("importId");
    const id = formData.get("id");
    const selected_dr_unique_code = formData.get("selected_dr_unique_code");
    const status = formData.get("status");

    const matchingUpdateResponse = await fetch(
      `/api/client/scores/${scoreId}/import_deposits/${importId}/matching_entries/${id}/`,
      {
        method: "PATCH",
        body: JSON.stringify({ data: { status, selected_dr_unique_code } }),
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (matchingUpdateResponse.status !== 200) {
      throw data("Failed to update the selected code for this matching", {
        status: matchingUpdateResponse.status,
      });
    }
  }

  if (request.method === "POST") {
    const formData = await request.formData();
    const scoreId = formData.get("scoreId");
    const importId = formData.get("importId");
    const matchingsIntegrationResponse = await fetch(
      `/api/client/scores/${scoreId}/import_deposits/${importId}/validate_matchings`,
      {
        method: "POST",
      }
    );

    if (matchingsIntegrationResponse.status !== 204) {
      return { integration: "fail" };
    }
    return { integration: "success" };
  }
}

function ImportDepositMatching() {
  const { t, i18n } = useTranslation("importDeposit");
  const { rawReferentials } = useImportDepositContext();
  const { toast } = useToast();
  const actionData = useActionData<typeof action>();
  const navigate = useNavigate();
  const submit = useSubmit();

  const { scoreId } = useParams();
  invariant(scoreId, "No scoreId found!");

  const { importDeposit, matchings, meta, url } =
    useLoaderData<typeof loader>();

  useEffect(() => {
    if (actionData?.integration === "success") {
      toast({
        title: t("importDeposit:integrationModal.successToast"),
        intent: "positive",
      });
      // TO DO : switch to useNavigate when the import_deposits React migration is deployed
      window.location.href = `/scores/${scoreId}/import_deposits`;
    } else if (actionData?.integration === "fail") {
      toast({
        title: t("importDeposit:integrationModal.failToast"),
        intent: "negative",
      });
    }
  }, [actionData]);

  const handleIntegration = () =>
    submit(
      {
        importId: importDeposit.id,
        scoreId: scoreId,
      },
      { method: "POST" }
    );

  const referentials = rawReferentials.map((referential) => {
    return {
      label: i18n.language === "fr" ? referential.name_fr : referential.name_en,
      value: referential.unique_code,
    };
  });

  const { featureFlags } = useContext(FeaturesContext);
  if (!featureFlags || !featureFlags["import_to_verify_interface"]) {
    return null;
  }

  const showContextColumn = importDeposit.import_type === "products";
  // Paginated table shows how many elements are displayed, for exemple "100-120 out of 150"
  const previousPagesMatchings =
    (meta.pagination.page - 1) * meta.pagination.limit;
  const matchingsShownedCount = previousPagesMatchings + matchings.length;

  return (
    <PageWrapper>
      <div className="mx-xl my-lg">
        <CardBackground className="my-lg flex">
          <div className="flex flex-1 items-center">
            <FileSpreadsheetIcon className="mr-xs text-primary-light" />
            <Typography step="2" weight="semibold">
              {importDeposit.input_file}
            </Typography>
          </div>
          <div className="mx-sm flex items-center justify-end">
            <Tag
              text={t("importDeposit:importMatching.matchesNumber", {
                x: `${t("formatter:number", { value: meta.items.total_pending })}`,
                y: `${t("formatter:number", { value: meta.items.total })}`,
              })}
              intent={"warning"}
            />
          </div>
          {meta.items.total_pending ? (
            <ConfirmIntegrationtModal handleIntegration={handleIntegration} />
          ) : (
            <Button intent="primary" onClick={handleIntegration} size="small">
              {t("importDeposit:integrationModal.triggerButton")}
            </Button>
          )}
        </CardBackground>

        <CardBackground className="my-lg">
          <Typography as="h4" step="2" weight="semibold" className="mb-md">
            {t("importDeposit:importMatching.matchesTitle")}
          </Typography>
          <Typography step="0" weight="normal" className="mb-md">
            {t("importDeposit:importMatching.matchesSubtitle")}
          </Typography>
          <Form>
            <Table
              isPaginated
              currentPage={meta.pagination.page}
              pageCount={meta.pagination.total_pages}
              displayedResultsMessage={t(
                `importDeposit:importMatchingTable.itemsDisplayed`,
                {
                  countFirst: previousPagesMatchings,
                  countSecond: matchingsShownedCount,
                  total: meta.items.total,
                }
              )}
              onPageChange={(newPage) => {
                navigate(`${url.pathname}?page=${newPage}`);
              }}
            >
              <Header>
                <Row>
                  {showContextColumn && (
                    <ColumnHeaderCell>
                      {t(`importDeposit:importMatchingTable.contextColumn`)}
                    </ColumnHeaderCell>
                  )}
                  <ColumnHeaderCell>
                    {t(
                      `importDeposit:importMatchingTable.contentNameColumn.${importDeposit.import_type}`
                    )}
                  </ColumnHeaderCell>
                  <ColumnHeaderCell>
                    {t(`importDeposit:importMatchingTable.emissionFactor`)}
                  </ColumnHeaderCell>
                  <ColumnHeaderCell className="w-3xl">
                    {t(`importDeposit:importMatchingTable.statusHeader`)}
                  </ColumnHeaderCell>
                  <ColumnHeaderCell className="text-right">
                    {t(`importDeposit:importMatchingTable.actions`)}
                  </ColumnHeaderCell>
                </Row>
              </Header>
              <Body>
                {matchings.map((matching) => (
                  <MatchingRow
                    key={matching.id}
                    matching={matching}
                    referentials={referentials}
                    showContextColumn={showContextColumn}
                  />
                ))}
              </Body>
            </Table>
          </Form>
        </CardBackground>
      </div>
    </PageWrapper>
  );
}

export default ImportDepositMatching;
